import { z } from "zod";
import { MODES, type Mode } from "./modes";
import { EYE_STYLES } from "./ranks";

/*
  CARD VARIANTS. Each strand has its own look of every ninja card. Earning a
  rank in a strand earns that strand's look of the card; the strand's ladder
  shows its looks. A variant is data: a colour change plus extra layers drawn
  around any rank's art (see art/ninjaSvg.ts for the layer drawings). To add
  a look (for example, a quest reward later), add a layer drawing and a row.

  name:       kid-facing look name
  prefix:     put the name before the rank name ("Shadow Storm Ninja")
  suitShade:  darken (negative) or lighten (positive) the suit, -1 to 1
  bgShade:    background from the rank colour: 0.72 is the normal light tint;
              negative is a dark, night-time background
  eyes, glow: eye style and glow colour, overriding the rank's
  layers:     extra drawings, in the order listed
*/

export const VARIANT_LAYERS = ["moon", "clones", "aura", "split", "blades", "smoke"] as const;
export type VariantLayer = (typeof VARIANT_LAYERS)[number];

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected a #rrggbb colour");

export const VariantSchema = z.object({
  name: z.string().min(1),
  prefix: z.boolean(),
  suitShade: z.number().min(-1).max(1).optional(),
  bgShade: z.number().min(-1).max(1).optional(),
  eyes: z.enum(EYE_STYLES).optional(),
  glow: hex.optional(),
  layers: z.array(z.enum(VARIANT_LAYERS)),
});
export type Variant = z.infer<typeof VariantSchema>;

export const VARIANTS: Readonly<Record<Mode, Variant>> = z
  .record(z.enum(MODES), VariantSchema)
  .refine((v) => MODES.every((m) => m in v), "one variant per strand")
  .parse({
    add: { name: "Classic", prefix: false, layers: [] },
    sub: {
      name: "Shadow",
      prefix: true,
      suitShade: -0.3,
      bgShade: -0.55,
      eyes: "glow",
      glow: "#C9A6FF",
      layers: ["moon", "smoke"],
    },
    mul: { name: "Clone", prefix: true, layers: ["clones"] },
    div: { name: "Split Blade", prefix: true, layers: ["split", "blades"] },
    mix: { name: "Elemental", prefix: true, glow: "#FFD23F", layers: ["aura"] },
  });

/** "Storm Ninja", "Shadow Storm Ninja". */
export function variantName(mode: Mode, rankName: string): string {
  const v = VARIANTS[mode];
  // "Shadow Cub" already starts with the Shadow look's name: don't double it.
  if (!v.prefix || rankName.startsWith(`${v.name} `)) return rankName;
  return `${v.name} ${rankName}`;
}
